import React, { useState, useEffect } from 'react';
import PageMeta from '../components/PageMeta';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Check, Shield, Star, Award, FileCheck, TrendingUp, ChevronRight, Sparkles, Zap, Clock, CheckCircle, Search } from 'lucide-react';
import { mockData } from '../mock';
import { toast } from 'sonner';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Link, useNavigate } from 'react-router-dom';
import Popup from '../components/Popup';
import MobileCardSlider from '../components/MobileCardSlider';
import useCounterAnimation from '../hooks/useCounterAnimation';
import { submitFormWithGeoLocation } from '../services/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Counter animations with intersection observer
  const [trademarksCount, trademarksRef] = useCounterAnimation(10000, 2000, 200);
  const [successCount, successRef] = useCounterAnimation(98, 2000, 200);
  const [statesCount, statesRef] = useCounterAnimation(50, 1500, 200);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Testimonial rotation
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % mockData.testimonials.length);
    }, 5000);
    
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const result = await submitFormWithGeoLocation(formData);
      
      if (result.success) {
        toast.success(result.message || 'Thank you! Your request has been submitted.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          contactNumber: '',
          message: ''
        });
        
        // Redirect to application page with customerId
        if (result.customerId) {
          setTimeout(() => {
            navigate(`/application/main?customerid=${result.customerId}`);
          }, 500);
        }
      } else {
        // Show error message
        toast.error(result.error || 'Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen">
      <PageMeta
        title="Trademark Registration - Protect Your Brand"
        description="Register your trademark today with Global Trademark Federation. Fast, affordable, and professional trademark registration services across all 50 US states. Get started now."
      />
      {/* Enhanced Header for Landing Page */}
      <header className="fixed top-0 left-0 right-0 bg-primary text-white shadow-2xl z-50">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="hover:scale-105 transition-transform cursor-pointer">
              <img src="/assets/img/brand/logo-light.png" alt="Global Trademark Federation" className="h-12 md:h-14" />
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <Button onClick={() => setIsPopupOpen(true)} className="bg-secondary hover:bg-secondary/90 text-white font-bold shadow-lg hover:shadow-secondary/50 transition-all hover:scale-110 group relative overflow-hidden">
                <span className="relative z-10 flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform" />
                  Register My Trademark
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </Button>
              <Button onClick={() => { if (window.LiveChatWidget) window.LiveChatWidget.call('maximize'); }} variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary font-bold transition-all hover:scale-105">
                <Zap className="w-4 h-4 mr-2" />
                Chat Now
              </Button>
            </div>
            <div className="md:hidden flex items-center gap-2">
              <Button
                data-testid="lp-mobile-chat-btn"
                onClick={() => { if (window.LiveChatWidget) window.LiveChatWidget.call('maximize'); }}
                variant="outline"
                className="border border-white text-white hover:bg-white hover:text-primary font-bold text-xs px-3 py-2 rounded-full transition-all"
              >
                <Zap className="w-3 h-3 mr-1 inline" />
                Chat Now
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Redesigned Hero Section - Better Alignment */}
      <section
        className="relative bg-cover bg-center pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden min-h-[85vh] flex items-center"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.85), rgba(45, 48, 71, 0.70)), url(${mockData.heroImages.lp})`,
        }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4em0tMTYgMGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4eiIvPjwvZz48L2c+PC9zdmc+')] opacity-10" />
        
        {/* Enhanced Floating elements */}
        <div className="hidden lg:block absolute top-10 left-10 w-32 h-32 bg-secondary/20 rounded-full blur-3xl" />
        <div className="hidden lg:block absolute bottom-10 right-10 w-40 h-40 bg-accent/20 rounded-full blur-3xl" />
        <div className="hidden lg:block absolute top-1/4 right-1/3 w-20 h-20 bg-secondary/30 rounded-lg blur-2xl" />
        <div className="hidden lg:block absolute bottom-1/4 left-1/3 w-16 h-16 bg-accent/30 rounded-full blur-xl" />
        
        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Enhanced Content with Better Alignment */}
              <div className="text-white flex flex-col justify-center">
                <div className="mb-6 animate-fade-in">
                  <span className="inline-flex items-center bg-gradient-to-r from-secondary/30 to-white/20 backdrop-blur-md text-white px-6 py-3 rounded-full text-sm font-bold border-2 border-white/40 shadow-xl gap-2">
                    <Star className="w-4 h-4 fill-secondary text-secondary animate-pulse" />
                    #1 Trademark Services in USA
                    <Star className="w-4 h-4 fill-secondary text-secondary animate-pulse" />
                  </span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
                  Protect Your Brand Legacy
                </h1>
                
                <p className="text-lg md:text-xl mb-8 opacity-95 leading-relaxed animate-fade-in max-w-xl" style={{ animationDelay: '0.2s' }}>
                  Professional trademark registration services across the USA. Secure your brand identity with our expert legal team.
                </p>
                
                <div className="space-y-4 mb-8 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                  {[
                    { icon: Check, text: 'Fast & Reliable Registration Process' },
                    { icon: Check, text: 'Expert Legal Guidance' },
                    { icon: Check, text: 'Affordable Pricing Starting at $45' },
                    { icon: Check, text: '98% Success Rate' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center group">
                      <div className="bg-white/20 backdrop-blur-sm w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-secondary group-hover:scale-110 transition-all">
                        <item.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-base md:text-lg group-hover:translate-x-2 transition-transform">{item.text}</span>
                    </div>
                  ))}
                </div>
                
                {/* Trust Badge */}
                <div className="flex items-center gap-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                  <div ref={trademarksRef} className="bg-white/20 backdrop-blur-md rounded-xl px-4 py-3 border border-white/30">
                    <div className="text-2xl font-bold text-secondary">{trademarksCount.toLocaleString()}+</div>
                    <div className="text-xs opacity-90">Trademarks Filed</div>
                  </div>
                  <div ref={successRef} className="bg-white/20 backdrop-blur-md rounded-xl px-4 py-3 border border-white/30">
                    <div className="text-2xl font-bold text-secondary">{successCount}%</div>
                    <div className="text-xs opacity-90">Success Rate</div>
                  </div>
                </div>
              </div>

              {/* Right Side - Centered & Enhanced Form */}
              <div className="flex items-center justify-center lg:justify-end">
                <Card className="bg-white shadow-2xl border-2 border-secondary/30 relative overflow-hidden w-full max-w-md animate-scale-in">
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-secondary/5 to-accent/5 rounded-full blur-3xl" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-accent/5 to-secondary/5 rounded-full blur-2xl" />
                  
                  <CardContent className="p-8 relative z-10">
                    <div className="text-center mb-8">
                      <div className="inline-flex items-center bg-gradient-to-r from-secondary via-accent to-secondary text-white px-6 py-2.5 rounded-full text-sm font-bold mb-4 shadow-lg animate-pulse">
                        <Sparkles className="w-4 h-4 mr-2 animate-spin" style={{ animationDuration: '3s' }} />
                        Limited Time Offer
                      </div>
                      <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-2">Start Your Registration</h2>
                      <p className="text-sm text-gray-600 flex items-center justify-center gap-2">
                        <Star className="w-4 h-4 text-secondary fill-secondary" />
                        Join 10,000+ satisfied clients
                      </p>
                    </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                      placeholder="Your full name"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      placeholder="your.email@example.com"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contact">Contact Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <select
                        value={formData.countryCode}
                        onChange={(e) => handleChange('countryCode', e.target.value)}
                        className="w-[140px] bg-white border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary"
                      >
                        {mockData.countryCodes.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.code} {item.country}
                          </option>
                        ))}
                      </select>
                      <Input
                        id="contact"
                        type="tel"
                        value={formData.contactNumber}
                        onChange={(e) => handleChange('contactNumber', e.target.value)}
                        required
                        placeholder="123-456-7890"
                        className="flex-1 bg-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="Tell us about your trademark needs..."
                      className="mt-1 min-h-[100px]"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold py-6 text-lg shadow-lg hover:shadow-secondary/50 transition-all hover:scale-105 group relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                      {isSubmitting ? 'Submitting...' : 'Register My Trademark'}
                      {!isSubmitting && <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />}
                    </span>
                    {!isSubmitting && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Third Informative Section - Brand Protection Benefits */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50 animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                Why Trademark Matters
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Protect What You've Built</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Your brand is your most valuable asset. Secure it with federal trademark protection and gain exclusive rights nationwide
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Left Side - Large Feature Card */}
            <div className="bg-white rounded-3xl p-10 shadow-2xl relative overflow-hidden group hover:shadow-3xl transition-all duration-500 border-2 border-gray-200">
              {/* Dark Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-10"
                style={{
                  backgroundImage: 'url("https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60")',
                }}
              />
              <div className="relative z-10">
                <div className="bg-secondary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-10 h-10 text-secondary" />
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Federal Legal Protection</h3>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Gain nationwide exclusive rights to your brand name, logo, or slogan. Stop competitors from using similar marks that could confuse customers.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <span className="text-gray-900">Presumption of ownership in all 50 states</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <span className="text-gray-900">Legal right to use ® symbol</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <span className="text-gray-900">Enhanced damages in infringement cases</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Side - Three Smaller Cards */}
            <div className="space-y-6">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:bg-primary/20 transition-all">
                    <TrendingUp className="w-7 h-7 text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-secondary transition-colors">Increase Brand Value</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Registered trademarks are valuable business assets that can be licensed, franchised, or sold
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group">
                <div className="flex items-start gap-4">
                  <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:bg-secondary/20 transition-all">
                    <Star className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-secondary transition-colors">Business Expansion</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Secure your brand before expanding to new markets, products, or services
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group">
                <div className="flex items-start gap-4">
                  <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:bg-secondary/20 transition-all">
                    <Award className="w-7 h-7 text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-secondary transition-colors">Consumer Trust</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Build credibility with customers who recognize registered trademarks as marks of quality
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden px-4">
            <MobileCardSlider showDots={true} autoScroll={false}>
              <div className="bg-white rounded-3xl p-8 shadow-2xl relative overflow-hidden border-2 border-gray-200">
                {/* Dark Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center opacity-10"
                  style={{
                    backgroundImage: 'url("https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60")',
                  }}
                />
                <div className="relative z-10">
                  <div className="bg-secondary/10 w-16 h-16 rounded-3xl flex items-center justify-center mb-4">
                    <Shield className="w-8 h-8 text-secondary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">Federal Legal Protection</h3>
                  <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                    Gain nationwide exclusive rights to your brand name, logo, or slogan
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-gray-900">Ownership in all 50 states</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-gray-900">Legal right to use ® symbol</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-gray-900">Enhanced damages protection</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10">
                <div className="bg-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Increase Brand Value</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Registered trademarks are valuable business assets that can be licensed, franchised, or sold
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10">
                <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Star className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Business Expansion</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Secure your brand before expanding to new markets, products, or services
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10">
                <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Award className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Consumer Trust</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Build credibility with customers who recognize registered trademarks as marks of quality
                </p>
              </div>
            </MobileCardSlider>
          </div>
        </div>
      </section>

      {/* Informative Section - Below Hero */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                Why Choose Global Trademark Federation
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Your Trusted Trademark Partner</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We simplify the trademark registration process with expert guidance, transparent pricing, and dedicated support every step of the way
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-primary/10 hover:border-secondary group cursor-pointer relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="bg-gradient-to-br from-secondary/10 to-accent/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <Shield className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">Comprehensive Protection</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Full trademark search, application filing, and monitoring services to protect your brand nationwide
                </p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-primary/10 hover:border-secondary group cursor-pointer relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="bg-gradient-to-br from-accent/20 to-secondary/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <Award className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">Expert Legal Team</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Experienced trademark attorneys and legal professionals guiding you through every step
                </p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-primary/10 hover:border-secondary group cursor-pointer relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="bg-gradient-to-br from-primary/10 to-accent/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <CheckCircle className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">Transparent Pricing</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Clear, upfront pricing with no hidden fees. Know exactly what you're paying for from the start
                </p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-primary/10 hover:border-secondary group cursor-pointer relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="bg-gradient-to-br from-secondary/10 to-primary/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <Zap className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">Fast Processing</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Quick turnaround times with 24/7 support to keep your trademark application moving forward
                </p>
              </div>
            </div>
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden px-4">
            <MobileCardSlider showDots={true} autoScroll={false}>
              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10 relative overflow-hidden">
                <div className="bg-gradient-to-br from-secondary/10 to-accent/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Shield className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Comprehensive Protection</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Full trademark search, application filing, and monitoring services to protect your brand nationwide
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10 relative overflow-hidden">
                <div className="bg-gradient-to-br from-accent/20 to-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Award className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Expert Legal Team</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Experienced trademark attorneys and legal professionals guiding you through every step
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10 relative overflow-hidden">
                <div className="bg-gradient-to-br from-primary/10 to-accent/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <CheckCircle className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Transparent Pricing</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Clear, upfront pricing with no hidden fees. Know exactly what you're paying for from the start
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10 relative overflow-hidden">
                <div className="bg-gradient-to-br from-secondary/10 to-primary/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Zap className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Fast Processing</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Quick turnaround times with 24/7 support to keep your trademark application moving forward
                </p>
              </div>
            </MobileCardSlider>
          </div>
        </div>
      </section>

      {/* Second Informative Section - How It Works */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:50px_50px]" />
        <div className="absolute top-10 left-10 w-72 h-72 bg-accent/10 rounded-full blur-3xl opacity-40" />
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl opacity-40" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                How It Works
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Three Simple Steps to Protection</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our streamlined process makes trademark registration easy and stress-free
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="relative group">
              <div className="bg-gradient-to-br from-white to-primary/5 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary">
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-gradient-to-br from-secondary to-accent rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-500">
                  01
                </div>
                <div className="mb-6">
                  <div className="bg-gradient-to-br from-secondary/10 to-accent/20 w-20 h-20 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Search className="w-10 h-10 text-secondary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Search & Consultation</h3>
                <p className="text-gray-600 leading-relaxed">
                  We conduct a comprehensive trademark search and provide expert consultation to ensure your mark is eligible for registration
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="bg-gradient-to-br from-white to-primary/5 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary">
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-gradient-to-br from-secondary to-accent rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-500">
                  02
                </div>
                <div className="mb-6">
                  <div className="bg-gradient-to-br from-accent/20 to-secondary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <FileCheck className="w-10 h-10 text-secondary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Application Filing</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our legal team prepares and files your trademark application with the USPTO, handling all the complex paperwork for you
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="bg-gradient-to-br from-white to-primary/5 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary">
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-gradient-to-br from-secondary to-accent rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-500">
                  03
                </div>
                <div className="mb-6">
                  <div className="bg-gradient-to-br from-primary/10 to-accent/20 w-20 h-20 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Shield className="w-10 h-10 text-secondary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Monitor & Protect</h3>
                <p className="text-gray-600 leading-relaxed">
                  We monitor your application status and provide ongoing support until your trademark is fully registered and protected
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Trust Indicators with Animation */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-primary mb-2">Trusted by Thousands</h3>
            <p className="text-gray-600">Join our community of successful trademark owners</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group cursor-pointer">
              <div className="text-3xl md:text-4xl font-bold text-secondary mb-2 group-hover:scale-110 transition-transform">{trademarksCount.toLocaleString()}+</div>
              <div className="text-sm md:text-base text-gray-600 font-medium">Trademarks Registered</div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group cursor-pointer">
              <div className="text-3xl md:text-4xl font-bold text-secondary mb-2 group-hover:scale-110 transition-transform">{successCount}%</div>
              <div className="text-sm md:text-base text-gray-600 font-medium">Success Rate</div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group cursor-pointer">
              <div className="text-3xl md:text-4xl font-bold text-secondary mb-2 group-hover:scale-110 transition-transform">24/7</div>
              <div className="text-sm md:text-base text-gray-600 font-medium">Support Available</div>
            </div>
            <div ref={statesRef} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group cursor-pointer">
              <div className="text-3xl md:text-4xl font-bold text-secondary mb-2 group-hover:scale-110 transition-transform">{statesCount}+</div>
              <div className="text-sm md:text-base text-gray-600 font-medium">States Covered</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trademark Registration Matters */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Trademark Registration Matters</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Protect your brand from infringement and establish legal ownership
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Legal Protection</h3>
                <p className="text-gray-600">
                  Federal registration provides nationwide protection and legal presumption of ownership for your brand.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Brand Value</h3>
                <p className="text-gray-600">
                  A registered trademark increases your brand's credibility and can significantly boost business value.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileCheck className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Exclusive Rights</h3>
                <p className="text-gray-600">
                  Gain exclusive rights to use your trademark and prevent others from using similar marks.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Business Growth</h3>
                <p className="text-gray-600">
                  A strong trademark foundation enables confident business expansion and franchise opportunities.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Online Protection</h3>
                <p className="text-gray-600">
                  Protect your brand on e-commerce platforms and prevent counterfeit products.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Asset Building</h3>
                <p className="text-gray-600">
                  Your trademark becomes a valuable business asset that appreciates over time.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden px-4">
            <MobileCardSlider showDots={true} autoScroll={false}>
              <Card className="hover:shadow-xl transition-shadow border-2 border-gray-200">
                <CardContent className="p-6 text-center">
                  <div className="bg-secondary/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Legal Protection</h3>
                  <p className="text-gray-600 text-sm">
                    Federal registration provides nationwide protection and legal presumption of ownership for your brand.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow border-2 border-gray-200">
                <CardContent className="p-6 text-center">
                  <div className="bg-secondary/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Brand Value</h3>
                  <p className="text-gray-600 text-sm">
                    A registered trademark increases your brand's credibility and can significantly boost business value.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow border-2 border-gray-200">
                <CardContent className="p-6 text-center">
                  <div className="bg-secondary/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileCheck className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Exclusive Rights</h3>
                  <p className="text-gray-600 text-sm">
                    Gain exclusive rights to use your trademark and prevent others from using similar marks.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow border-2 border-gray-200">
                <CardContent className="p-6 text-center">
                  <div className="bg-secondary/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Business Growth</h3>
                  <p className="text-gray-600 text-sm">
                    A strong trademark foundation enables confident business expansion and franchise opportunities.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow border-2 border-gray-200">
                <CardContent className="p-6 text-center">
                  <div className="bg-secondary/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Online Protection</h3>
                  <p className="text-gray-600 text-sm">
                    Protect your brand on e-commerce platforms and prevent counterfeit products.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow border-2 border-gray-200">
                <CardContent className="p-6 text-center">
                  <div className="bg-secondary/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Asset Building</h3>
                  <p className="text-gray-600 text-sm">
                    Your trademark becomes a valuable business asset that appreciates over time.
                  </p>
                </CardContent>
              </Card>
            </MobileCardSlider>
          </div>
        </div>
      </section>

      {/* CTA Banner - Middle of Page */}
      <section className="py-20 relative overflow-hidden" style={{ backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.95), rgba(45, 48, 71, 0.85))` }}>
        {/* Abstract Background Pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            backgroundSize: '100px 100px'
          }}
        />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block mb-6 animate-bounce">
              <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border border-white/30">
                <span className="text-white font-bold text-sm flex items-center gap-2 justify-center">
                  <Sparkles className="w-5 h-5" />
                  Limited Time Offer
                </span>
              </div>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Ready to Protect Your Brand?
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed max-w-3xl mx-auto">
              Join thousands of successful businesses who trust us with their trademark registration. Get started today and secure your brand's future.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={() => setIsPopupOpen(true)}
                size="lg"
                className="bg-secondary hover:bg-secondary/90 text-white text-lg px-8 py-6 rounded-full shadow-2xl hover:shadow-secondary/50 hover:scale-105 transition-all duration-300 font-bold group"
              >
                Get Started Now
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => setIsPopupOpen(true)}
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-md border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-6 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 font-bold"
              >
                Schedule Consultation
              </Button>
            </div>
            
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-secondary" />
                <span className="font-semibold">No Hidden Fees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-secondary" />
                <span className="font-semibold">Expert Support</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-secondary" />
                <span className="font-semibold">Fast Processing</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Transparent Pricing</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the package that best fits your needs - no hidden fees
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {mockData.pricingPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative flex flex-col h-full transition-all hover:shadow-xl ${
                  plan.recommended ? 'border-secondary border-2 shadow-lg transform scale-105' : 'border-gray-200'
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-secondary text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Recommended
                    </div>
                  </div>
                )}
                
                <CardContent className="p-6 flex flex-col flex-grow">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold text-secondary">{plan.price}</div>
                    <div className="text-sm text-gray-600 mt-1">{plan.filingFee}</div>
                  </div>
                  
                  <div className="flex-grow overflow-y-auto max-h-64 mb-4">
                    <ul className="space-y-2">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm">
                          <Check className="w-5 h-5 text-secondary mr-2 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="text-xs text-gray-500 mb-4 italic text-center">{plan.note}</div>
                  
                  <Button
                    onClick={() => setIsPopupOpen(true)}
                    className={`w-full font-semibold ${
                      plan.recommended
                        ? 'bg-secondary hover:bg-secondary/90 text-white'
                        : 'bg-gray-100 hover:bg-secondary hover:text-white text-gray-900'
                    }`}
                  >
                    Select Plan
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by thousands of businesses across the United States
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="bg-white shadow-lg">
              <CardContent className="p-12">
                <div className="flex justify-center mb-4">
                  {[...Array(mockData.testimonials[currentTestimonial].rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-secondary text-secondary" />
                  ))}
                </div>
                
                <p className="text-xl text-gray-700 text-center mb-6 italic">
                  "{mockData.testimonials[currentTestimonial].text}"
                </p>
                
                <div className="text-center">
                  <p className="font-bold text-gray-900 text-lg">{mockData.testimonials[currentTestimonial].name}</p>
                  <p className="text-gray-600">{mockData.testimonials[currentTestimonial].company}</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center mt-8 space-x-2">
              {mockData.testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentTestimonial ? 'bg-secondary w-8' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about trademark registration
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-gray-50 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-secondary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Enhanced Final CTA */}
      <section className="py-20 md:py-24 text-white relative overflow-hidden" style={{ backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.95), rgba(45, 48, 71, 0.85))` }}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4em0tMTYgMGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4eiIvPjwvZz48L2c+PC9zdmc+')] opacity-10" />
        
        {/* Floating elements */}
        <div className="hidden md:block absolute top-10 left-10 w-32 h-32 bg-secondary/20 rounded-full blur-2xl" />
        <div className="hidden md:block absolute bottom-10 right-10 w-24 h-24 bg-accent/30 rounded-full blur-xl" />
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-block mb-6 animate-fade-in">
            <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mx-auto">
              <Shield className="w-12 h-12 text-secondary" />
            </div>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>Ready to Protect Your Brand?</h2>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto opacity-95 leading-relaxed animate-fade-in" style={{ animationDelay: '0.4s' }}>
            Join thousands of satisfied clients who have secured their trademarks with us
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <Button onClick={() => setIsPopupOpen(true)} size="lg" className="bg-secondary text-white hover:bg-secondary/90 font-bold text-base md:text-lg px-8 py-6 md:py-7 shadow-2xl hover:shadow-secondary/50 hover:scale-110 transition-all group relative overflow-hidden">
              <span className="relative z-10 flex items-center">
                <Sparkles className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                Register My Trademark
                <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </Button>
            <Button onClick={() => setIsPopupOpen(true)} size="lg" variant="outline" className="bg-white/10 backdrop-blur-md border-2 border-white text-white hover:bg-white hover:text-primary font-bold text-base md:text-lg px-8 py-6 md:py-7 transition-all hover:scale-105">
              <Zap className="w-5 h-5 mr-2" />
              Chat Now
            </Button>
          </div>
        </div>
      </section>
      
      {/* Simplified Footer */}
      <footer className="bg-primary text-gray-300 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-3">
            <p className="text-sm">© 2026 Global Trademark Federation. All rights reserved.</p>
            <div className="flex items-center justify-center gap-4 text-sm">
              <Link to="/privacy-policy" className="hover:text-secondary transition-colors">
                Privacy Policy
              </Link>
              <span>|</span>
              <Link to="/terms-of-service" className="hover:text-secondary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Popup Component */}
      <Popup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default LandingPage;
